/* eslint-disable react-hooks/refs, react-hooks/exhaustive-deps */
import { ifDefined, isDefined, Maybe } from './MaybeV2';
import { shallowEqual } from './Object';
import React, {
    type JSX,
    useContext,
    useRef,
    useState,
    useLayoutEffect,
    useCallback,
    memo,
    useEffect,
} from 'react';

function deepClone<T>(value: T, stack = new WeakMap<object, unknown>()): T {
    if (value === null || typeof value !== 'object') {
        return value;
    }

    if (stack.has(value as object)) {
        return stack.get(value as object) as T;
    }

    if (value instanceof Date) {
        return new Date(value.getTime()) as unknown as T;
    }

    if (value instanceof RegExp) {
        return new RegExp(value.source, value.flags) as unknown as T;
    }

    if (value instanceof Map) {
        const result = new Map();
        stack.set(value as object, result);
        value.forEach((v, k) => {
            result.set(
                deepClone(k as unknown as object, stack),
                deepClone(v as unknown as object, stack),
            );
        });
        return result as unknown as T;
    }

    if (value instanceof Set) {
        const result = new Set();
        stack.set(value as object, result);
        value.forEach(v => {
            result.add(deepClone(v as unknown as object, stack));
        });
        return result as unknown as T;
    }

    if (Array.isArray(value)) {
        const result: unknown[] = [];
        stack.set(value as object, result);
        value.forEach((item, idx) => {
            result[idx] = deepClone(item as unknown as object, stack);
        });
        return result as unknown as T;
    }

    if (ArrayBuffer.isView(value)) {
        const bufferClone = value.buffer.slice(0);
        if (value instanceof DataView) {
            return new DataView(
                bufferClone,
                value.byteOffset,
                value.byteLength,
            ) as unknown as T;
        }
        const ctor = value.constructor as {
            new (
                buffer: ArrayBufferLike,
                byteOffset: number,
                length?: number,
            ): typeof value;
        };
        const typed = value as ArrayBufferView & { length?: number };
        return new ctor(
            bufferClone,
            typed.byteOffset,
            typed.length,
        ) as unknown as T;
    }

    if (value instanceof ArrayBuffer) {
        return value.slice(0) as unknown as T;
    }

    const proto = Object.getPrototypeOf(value);
    const result = Object.create(proto);
    stack.set(value as object, result);

    const source = value as Record<PropertyKey, unknown>;
    const target = result as Record<PropertyKey, unknown>;

    Reflect.ownKeys(source).forEach(key => {
        target[key] = deepClone(source[key] as unknown as object, stack);
    });

    return result as T;
}

interface ChangeCallback {
    (): void;
}

interface AfterUpdateHandler {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (store: Store, name: string, args: Maybe<any>): void;
}

export class Store {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private state: { [idx: string]: any };
    private callbacks: ChangeCallback[];
    private afterUpdateHandler: Maybe<AfterUpdateHandler>;

    constructor(afterUpdateHandler?: Maybe<AfterUpdateHandler>) {
        this.callbacks = [];
        this.state = {};
        this.update = this.update.bind(this);
        this.legacyUpdater = this.legacyUpdater.bind(this);
        this.afterUpdateHandler = afterUpdateHandler;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutate(key: string, value: any): void {
        this.state[key] = value;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get(key: string): any {
        return this.state[key];
    }

    addChangeListener(callback: ChangeCallback): void {
        this.callbacks.push(callback);
    }

    removeChangeListener(callback: ChangeCallback): void {
        const index = this.callbacks.indexOf(callback);
        if (index > -1) {
            this.callbacks.splice(index, 1);
        }
    }

    update<V>(update: Update<V>, args?: V): void {
        const name = update(this, args);
        this.emitChange();
        ifDefined(this.afterUpdateHandler, (h: AfterUpdateHandler) =>
            h(this, name, args),
        );
    }

    legacyUpdater<V>(update: LegacyUpdate<V>, args?: V): void {
        this.update((store: Store, args?: V) => {
            update(args)(store);
            return 'LEGACY_UPDATE';
        }, args);
    }

    emitChange(): void {
        this.callbacks.forEach((cb: ChangeCallback) => cb());
    }
}

export interface LegacyUpdate<V> {
    (args?: V): Write;
}

export interface Updater {
    <V>(update: Update<V>, args?: V): void;
}

export interface UpdateProp {
    update: Updater;
}

export interface Update<V> {
    (store: Store, args?: V): string;
}

export interface Write {
    (store: Store): void;
}

export interface ContainerProps {
    key?: React.Key;
    allState: Store;
    preStateSetCallback?: () => void;
}

export function useUpdate() {
    const store = useContext(StoreContext);
    if (!store) {
        throw new Error(
            'using select state without allState injected into Context!! adjust main.ts',
        );
    }
    return store.update;
}

/**
 * @deprecated
 */
export function useStore<S>(selector: (store: Store) => S) {
    const store = useContext(StoreContext);

    /* we need to store the selector to make the current one callable from the memoized callback */
    const selectorRef = useRef(selector);
    selectorRef.current = selector;

    if (!store) {
        throw new Error(
            'using select state without allState injected into Context!! adjust main.ts',
        );
    }

    const [storeState, setState] = useState(selector(store));

    /*
    generate a memoized callback that allows clean adding and removing to the change listener
    */
    const refreshState = useCallback(() => {
        setState(oldState => {
            const newState = selectorRef.current(store);
            if (!shallowEqual(newState, oldState)) {
                return newState;
            } else {
                /* returning old state will not refresh state */
                return oldState;
            }
        });
    }, [] /* we never need to refresh this. current state selector gets passed in via ref */);

    /*
    equivalent  of component will mount and will unmount
    */
    useLayoutEffect(
        () => {
            store.addChangeListener(refreshState);
            return () => {
                store.removeChangeListener(refreshState);
            };
        },
        [
            refreshState,
        ] /*just in case the refresh callback somehow changes anyways */,
    );
    return { storeState, update: store.update, store };
}

export type SelectStateProps<P, S> = P & S & { update: Updater };

/**
 * @deprecated
 */
export function selectState<P extends object, S>(
    selector: (store: Store, props: P) => S,
    Comp: React.ComponentType<SelectStateProps<P, S>>,
    lifecycle?: {
        didMount?: (store: Store, props: P) => void;
        willUnmount?: (store: Store, props: P) => void;
    },
): React.ComponentType<P> {
    function getPropsForComponent(props: P & { allState: Store }): P {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const p = { ...(props as any) };
        delete p.allState;
        return p;
    }

    function SelectStateContainer(props: P & { allState: Store }) {
        const store = props.allState;
        const propsForComponent = getPropsForComponent(props);

        /* the current props have to be reachable from the memoized change
           listener and from the unmount cleanup without re-subscribing on
           every render - the same trick useStore uses for its selector */
        const currentProps = useRef({ props, propsForComponent });
        currentProps.current = { props, propsForComponent };

        const [selectedState, setSelectedState] = useState<S>(() =>
            selector(store, propsForComponent),
        );

        const refreshState = useCallback(() => {
            setSelectedState(oldState => {
                const newState = selector(
                    store,
                    currentProps.current.propsForComponent,
                );
                if (!shallowEqual(newState, oldState)) {
                    return newState;
                } else {
                    /* returning old state will not refresh state */
                    return oldState;
                }
            });
        }, [store]);

        /* was componentDidUpdate: the selector may read props, so it has to run
           again when they change. Running it during render rather than after
           the commit means the wrapped component no longer gets one render of
           stale state after every prop change. */
        const [previousProps, setPreviousProps] = useState(propsForComponent);
        if (!shallowEqual(previousProps, propsForComponent)) {
            setPreviousProps(propsForComponent);
            refreshState();
        }

        useLayoutEffect(() => {
            store.addChangeListener(refreshState);
            return () => {
                store.removeChangeListener(refreshState);
            };
        }, [store, refreshState]);

        /* The lifecycle hooks used to live on a separate wrapper component, so
           they ran around the subscription: mount subscribed first and then
           called didMount, unmount called willUnmount and then unsubscribed.
           The first order is preserved (layout effects run before effects); on
           unmount the subscription is now dropped first, so a willUnmount that
           writes to the store can no longer schedule an update on a component
           that is going away. */
        useEffect(() => {
            lifecycle?.didMount?.(store, currentProps.current.props);
            return () => {
                lifecycle?.willUnmount?.(store, currentProps.current.props);
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [store]);

        return (
            <Comp
                {...propsForComponent}
                update={store.update}
                {...selectedState}
            />
        );
    }

    /* The class this replaced bailed out in shouldComponentUpdate unless its
       props or its selected state had shallow-changed. The state half of that
       is covered by refreshState returning the old state, which makes React
       skip the re-render; memoizing covers the props half, so a parent
       re-rendering with shallow-equal props still does not reach the wrapped
       component. It compares the same props object the class compared. */
    const MemoizedContainer = memo(SelectStateContainer);

    return function SelectState(p: P) {
        const store = useContext(StoreContext);
        if (!store) {
            throw new Error(
                'using select state without allState injected into Context!! adjust main.ts',
            );
        }
        return <MemoizedContainer allState={store} {...p} />;
    };
}

export type LocalSetState<S> = (stateWrite: Partial<S>) => void;
export type LocalStateProps<S> = { state: S; setState: LocalSetState<S> };

type StateFun<P, S> = (props: P) => S;

export function localState<P extends object, S extends object = object>(
    initialState: S | StateFun<P, S>,
    Comp: React.ComponentType<P & LocalStateProps<S>>,
) {
    return function LocalState(props: P) {
        const [state, setState] = useState<S>(() =>
            typeof initialState === 'function'
                ? (initialState as StateFun<P, S>)(props)
                : initialState,
        );

        /* the class version relied on this.setState's shallow merge */
        const writeState = useCallback(
            (stateWrite: Partial<S>) =>
                setState(oldState => ({ ...oldState, ...stateWrite })),
            [],
        );

        return <Comp {...props} state={state} setState={writeState} />;
    };
}

export const ElementNamer = (component: string) => (block?: string) =>
    `${component}${block ? '-' : ''}${block ? block : ''}`;

export const ElementNamerGenerator = (file: string) => (component?: string) =>
    ElementNamer(`${file}${component ? component : ''}`);

/**
 * @deprecated
 */
export abstract class StateSlice<S> {
    private store: Store;
    private sideEffectsCalling: boolean;

    constructor(store: Store) {
        this.sideEffectsCalling = false;
        this.store = store;
    }

    get state(): S {
        this.initializeStore();
        // prevent recursion when accessing this.state in sideEffects
        if (!this.sideEffectsCalling) {
            this.sideEffectsCalling = true;
            this.sideEffects(this.store);
            this.sideEffectsCalling = false;
        }
        return this.store.get(this.key());
    }

    abstract key(): string;

    abstract getInitialState(store?: Store): S;

    abstract sideEffects(store?: Store): void;

    set(trans: (s: S) => S): void {
        this.initializeStore();
        this.mutate(trans(Object.assign({}, this.store.get(this.key()))));
    }

    reset(): void {
        this.initializeStore();
        this.mutate(this.getInitialState(this.store));
    }

    private initializeStore(): void {
        if (!this.store.get(this.key())) {
            this.mutate(this.getInitialState(this.store));
        }
    }

    private mutate(s: S): void {
        if (isDefined(s)) {
            Object.freeze(s);
            this.store.mutate(this.key(), s);
        } else {
            throw new Error(`${this.key()} state tried to mutate undefined`);
        }
    }
}

interface StateTransaction<S> {
    (s: S): S;
}

export interface SliceGenFuns<S> {
    get: (store: Store) => S;
    set: (store: Store, trans: StateTransaction<S>) => void;
    stateWrite: (store: Store, state: Partial<S>) => string;
    reset: (store: Store) => string;
    use: () => [S, (state: Partial<S>) => void, () => void];
}

/**
 * @deprecated
 */
export const generateState = <S extends object>(
    key: string,
    initialState: S | (() => S),
    sideEffects?: Maybe<(store: Store, state: S) => void>,
): SliceGenFuns<S> => {
    class GeneratedStateSlice extends StateSlice<S> {
        getInitialState(): S {
            if (typeof initialState === 'function') {
                return (initialState as () => S)();
            } else {
                return deepClone(initialState);
            }
        }

        key(): string {
            return key;
        }

        sideEffects(store: Store): void {
            if (isDefined(sideEffects)) {
                sideEffects(store, this.state);
            }
        }
    }

    const get = (store: Store): S => {
        return new GeneratedStateSlice(store).state;
    };
    const reset = (store: Store): string => {
        new GeneratedStateSlice(store).reset();
        return key + '-reset';
    };
    const stateWrite = (store: Store, state?: Partial<S>): string => {
        new GeneratedStateSlice(store).set((s: S): S =>
            Object.assign(s, state as S),
        );
        return key + '-stateWrite';
    };
    return {
        get,
        set: (store: Store, trans: StateTransaction<S>): void =>
            new GeneratedStateSlice(store).set(trans),
        reset,
        stateWrite,
        use: () => {
            const { storeState, update } = useStore(s => ({ state: get(s) }));
            return [
                storeState.state,
                (p: Partial<S>) => update(stateWrite, p),
                () => update(reset),
            ];
        },
    };
};

export function withUpdater<P, S>(
    Comp:
        | (new () => React.Component<P & S & UpdateProp, S>)
        | ((p: P & UpdateProp) => JSX.Element),
): React.FunctionComponent<P> {
    // eslint-disable-next-line react/display-name
    return (props: P) => {
        return (
            <StoreContext.Consumer>
                {store => {
                    if (!store) {
                        throw new Error(
                            'using select state without allState injected into Context!! adjust main.ts',
                        );
                    }
                    return <Comp update={store.update} {...props} />;
                }}
            </StoreContext.Consumer>
        );
    };
}

export const StoreContext = React.createContext<Store | undefined>(undefined);
